import { http } from "@/lib/http";
import type { PrecheckReport } from "@/lib/prechecks";

import type {
  ComputeZone,
  ConsoleOutput,
  ConsoleResponse,
  FlavorSummary,
  HighAvailabilityRequest,
  InstanceMetrics,
  InterfaceAttachRequest,
  ServerEvent,
  ServerInterface,
  ServerTrunk,
  SubportAddRequest,
  ImageSummary,
  Keypair,
  KeypairCreateRequest,
  KeypairCreateResponse,
  NetworkSummary,
  PaginatedServers,
  PortSecurityView,
  SecurityGroupOption,
  ServerActionRequest,
  ServerActionResponse,
  ServerCreateRequest,
  ServerCreateResponse,
  ServerDetail,
} from "./types";

export async function listFlavors(): Promise<FlavorSummary[]> {
  const { data } = await http.get<FlavorSummary[]>("/compute/flavors");
  return data;
}

export async function listImages(): Promise<ImageSummary[]> {
  const { data } = await http.get<ImageSummary[]>("/compute/images");
  return data;
}

export async function listAvailabilityZones(): Promise<ComputeZone[]> {
  const { data } = await http.get<ComputeZone[]>(
    "/compute/availability-zones",
  );
  return data;
}

export async function listNetworks(): Promise<NetworkSummary[]> {
  const { data } = await http.get<NetworkSummary[]>("/compute/networks");
  return data;
}

export async function listKeypairs(): Promise<Keypair[]> {
  const { data } = await http.get<Keypair[]>("/compute/keypairs");
  return data;
}

export async function createKeypair(
  payload: KeypairCreateRequest,
): Promise<KeypairCreateResponse> {
  const { data } = await http.post<KeypairCreateResponse>(
    "/compute/keypairs",
    payload,
  );
  return data;
}

export async function deleteKeypair(name: string): Promise<void> {
  await http.delete(`/compute/keypairs/${encodeURIComponent(name)}`);
}

export interface ServerListFilters {
  /** Case-insensitive substring match on the server name. */
  name?: string;
  /** Case-insensitive exact status match (e.g. "ACTIVE"). */
  status?: string;
}

export async function listServers(params: {
  page: number;
  pageSize: number;
  filters?: ServerListFilters;
}): Promise<PaginatedServers> {
  const { data } = await http.get<PaginatedServers>("/compute/servers", {
    params: {
      page: params.page,
      page_size: params.pageSize,
      // Filtered server-side before pagination (`total` is the filtered
      // count). FastAPI ignores unknown params, so this degrades gracefully
      // until the backend support lands.
      name: params.filters?.name || undefined,
      status: params.filters?.status || undefined,
    },
  });
  return data;
}

/** Turn automatic restart-on-failure on or off. 422 for an image-booted
 *  server — the UI disables the switch first, this is the backstop. */
export async function setServerHighAvailability(
  serverId: string,
  body: HighAvailabilityRequest,
): Promise<ServerDetail> {
  const { data } = await http.put<ServerDetail>(
    `/compute/servers/${serverId}/high-availability`,
    body,
  );
  return data;
}

export async function getInstanceMetrics(
  serverId: string,
  hours: number,
): Promise<InstanceMetrics> {
  const { data } = await http.get<InstanceMetrics>(
    `/metrics/instances/${serverId}`,
    { params: { hours } },
  );
  return data;
}

export async function listInterfaces(
  serverId: string,
): Promise<ServerInterface[]> {
  const { data } = await http.get<ServerInterface[]>(
    `/compute/servers/${serverId}/interfaces`,
  );
  return data;
}

export async function attachInterface(
  serverId: string,
  body: InterfaceAttachRequest,
): Promise<ServerInterface> {
  const { data } = await http.post<ServerInterface>(
    `/compute/servers/${serverId}/interfaces`,
    body,
  );
  return data;
}

export async function detachInterface(
  serverId: string,
  portId: string,
): Promise<void> {
  await http.delete(`/compute/servers/${serverId}/interfaces/${portId}`);
}

// --- Additional networks (VLAN) -------------------------------------- //

export async function getServerTrunk(serverId: string): Promise<ServerTrunk> {
  const { data } = await http.get<ServerTrunk>(
    `/compute/servers/${serverId}/trunk`,
  );
  return data;
}

export async function addSubport(
  serverId: string,
  body: SubportAddRequest,
): Promise<ServerTrunk> {
  const { data } = await http.post<ServerTrunk>(
    `/compute/servers/${serverId}/trunk/subports`,
    body,
  );
  return data;
}

export async function removeSubport(
  serverId: string,
  portId: string,
): Promise<void> {
  await http.delete(`/compute/servers/${serverId}/trunk/subports/${portId}`);
}

export async function getServer(serverId: string): Promise<ServerDetail> {
  const { data } = await http.get<ServerDetail>(
    `/compute/servers/${serverId}`,
  );
  return data;
}

export async function createServer(
  payload: ServerCreateRequest,
): Promise<ServerCreateResponse> {
  const { data } = await http.post<ServerCreateResponse>(
    "/compute/servers",
    payload,
  );
  return data;
}

/** Dry run of a launch: every prerequisite and what to fix. Creates nothing. */
export async function precheckServer(payload: ServerCreateRequest): Promise<PrecheckReport> {
  const { data } = await http.post<PrecheckReport>("/compute/servers/precheck", payload);
  return data;
}

export async function deleteServer(serverId: string): Promise<void> {
  await http.delete(`/compute/servers/${serverId}`);
}

export async function dispatchServerAction(
  serverId: string,
  payload: ServerActionRequest,
): Promise<ServerActionResponse> {
  const { data } = await http.post<ServerActionResponse>(
    `/compute/servers/${serverId}/actions`,
    payload,
  );
  return data;
}

export async function getConsole(serverId: string): Promise<ConsoleResponse> {
  const { data } = await http.get<ConsoleResponse>(
    `/compute/servers/${serverId}/console`,
  );
  return data;
}

export async function renameServer(
  serverId: string,
  name: string,
): Promise<ServerDetail> {
  const { data } = await http.put<ServerDetail>(`/compute/servers/${serverId}`, {
    name,
  });
  return data;
}

export async function getServerMetadata(
  serverId: string,
): Promise<Record<string, string>> {
  const { data } = await http.get<Record<string, string>>(
    `/compute/servers/${serverId}/metadata`,
  );
  return data;
}

export async function setServerMetadata(
  serverId: string,
  metadata: Record<string, string>,
): Promise<Record<string, string>> {
  const { data } = await http.put<Record<string, string>>(
    `/compute/servers/${serverId}/metadata`,
    { metadata },
  );
  return data;
}

export async function deleteServerMetadataItem(
  serverId: string,
  key: string,
): Promise<void> {
  await http.delete(
    `/compute/servers/${serverId}/metadata/${encodeURIComponent(key)}`,
  );
}

export async function getConsoleLog(serverId: string): Promise<ConsoleOutput> {
  const { data } = await http.get<ConsoleOutput>(
    `/compute/servers/${serverId}/console-log`,
  );
  return data;
}

// --- Per-interface security groups ------------------------------------- //
// These call the existing /network endpoints (ports are Neutron objects);
// compute keeps its own thin client so the instance Networking tab doesn't
// reach into the networks feature.

export async function getPortSecurity(
  portId: string,
): Promise<PortSecurityView> {
  const { data } = await http.get<PortSecurityView>(`/network/ports/${portId}`);
  return data;
}

export async function updatePortSecurityGroups(
  portId: string,
  securityGroupIds: string[],
): Promise<PortSecurityView> {
  const { data } = await http.put<PortSecurityView>(
    `/network/ports/${portId}`,
    { security_group_ids: securityGroupIds },
  );
  return data;
}

export async function listSecurityGroupOptions(): Promise<
  SecurityGroupOption[]
> {
  const { data } = await http.get<SecurityGroupOption[]>(
    "/network/security-groups",
  );
  return data;
}

export async function getServerEvents(
  serverId: string,
): Promise<ServerEvent[]> {
  const { data } = await http.get<ServerEvent[]>(
    `/compute/servers/${serverId}/events`,
  );
  return data;
}
